import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Menu, X, ShieldCheck } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Главная" },
  { to: "/catalog", label: "Каталог" },
  { to: "/services", label: "Услуги" },
  { to: "/news", label: "Новости" },
  { to: "/about", label: "О компании" },
  { to: "/contacts", label: "Контакты" },
];

export default function Header() {
  const { settings } = useSettings();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const s = settings || {};

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      {/* top bar */}
      <div className="hidden md:block bg-slate-50 border-b border-slate-100 text-slate-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-xs py-2">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><MapPin size={13} className="text-[#0E7C86]" /> {s.address}</span>
            <span className="flex items-center gap-1.5"><Clock size={13} className="text-[#0E7C86]" /> {s.work_hours}</span>
          </div>
          <div className="flex items-center gap-6">
            <a href={`tel:${s.phone_link}`} className="flex items-center gap-1.5 hover:text-[#E85D2A] transition-colors"><Phone size={13} /> {s.phone}</a>
            <a href={`mailto:${s.email}`} className="flex items-center gap-1.5 hover:text-[#E85D2A] transition-colors"><Mail size={13} /> {s.email}</a>
          </div>
        </div>
      </div>

      {/* main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <div className="w-9 h-9 rounded-lg bg-[#0E7C86] text-white flex items-center justify-center font-display font-bold text-sm tracking-tight">ПП</div>
          <div className="leading-none">
            <div className="font-display font-bold text-base text-[#0F1720]">{s.company_name || "Пром-Пластик"}</div>
            <div className="hidden sm:block text-[9px] tracking-[0.16em] text-slate-400 font-medium mt-0.5">{s.tagline}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0E7C86]/10 text-[#0E7C86]" : "text-slate-700 hover:text-[#E85D2A]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user && (
            <Link to="/admin" data-testid="header-admin-link" className="hidden sm:inline-flex items-center gap-2 bg-[#0F1720] hover:bg-slate-800 text-white rounded-full pl-4 pr-5 py-2.5 text-sm font-medium transition-all">
              <ShieldCheck size={15} /> Админ
            </Link>
          )}
          <a href={`tel:${s.phone_link}`} data-testid="call-cta" className="hidden sm:inline-flex items-center gap-2 bg-[#E85D2A] hover:bg-[#D04A1B] text-white rounded-full pl-4 pr-5 py-2.5 text-sm font-medium transition-all hover:shadow-lg hover:shadow-orange-500/30 group">
            <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center group-hover:rotate-12 transition-transform"><Phone size={13} /></span>
            Заказать звонок
          </a>
          <button className="lg:hidden text-[#0F1720]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Меню">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg text-sm font-medium ${isActive ? "bg-[#0E7C86]/10 text-[#0E7C86]" : "text-slate-700"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <a href={`tel:${s.phone_link}`} className="block bg-[#E85D2A] text-white text-center rounded-full px-5 py-3 text-sm font-medium mt-2">
            Позвонить: {s.phone}
          </a>
          {user && (
            <Link to="/admin" onClick={() => setOpen(false)} data-testid="header-admin-link-mobile" className="flex items-center justify-center gap-2 bg-[#0F1720] text-white text-center rounded-full px-5 py-3 text-sm font-medium mt-2">
              <ShieldCheck size={15} /> Админ-панель
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
